import { Injectable, inject } from '@angular/core';
import {
  Observable,
  BehaviorSubject,
  tap,
  of,
  throwError,
  map,
} from 'rxjs';
import { Router } from '@angular/router';
import {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  PasswordResetRequest,
  OtpVerificationRequest,
  PasswordUpdateRequest,
  ApiResponse,
  resolveUserAvatarUrl,
} from '@victory-lib-ui/shared/models';
import {
  StorageService,
  LoggerService,
  StorageType,
} from '@victory-lib-ui/shared/utils';
import {
  BaseApiService,
} from '@victory-lib-ui/shared/data-access';

/**
 * Authentication Service
 * Manages user authentication state and operations
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApiService {
  private readonly storage = inject(StorageService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);

  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);

  constructor() {
    super('auth'); // Base URL: {apiUrl}/auth (e.g., http://victorylib.test/api/v1/auth)
    // Load user from storage on service initialization
    this.loadUserFromStorage();
  }

  /**
   * Current user observable
   */
  readonly currentUser$ = this.currentUserSubject.asObservable();
  readonly currentUserAvatar$ = this.currentUser$.pipe(
    map((user) => this.getResolvedAvatarUrl(user))
  );

  /**
   * Check if user is authenticated
   */
  get isAuthenticated(): boolean {
    return !!this.currentUserSubject.value;
  }

  /**
   * Get current user
   */
  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  syncCurrentUser(user: User): void {
    this.currentUserSubject.next(user);
    this.persistUser(user);
  }

  getResolvedAvatarUrl(user: User | null = this.currentUser): string {
    return resolveUserAvatarUrl(user);
  }

  /**
   * Login user
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.logger.info('AuthService: Login attempt', {
      email: credentials.email,
    });

    return this.post<LoginResponse>('/login', {
      email: credentials.email,
      password: credentials.password,
      remember_me: credentials.rememberMe ?? false,
    }).pipe(
      tap((response) => {
        if (
          response.success &&
          response.data?.user &&
          response.data?.access_token
        ) {
          this.handleAuthSuccess(
            response.data.user,
            response.data.access_token,
            credentials.rememberMe,
            response.data.refresh_token
          );
          this.logger.info('Login successful', {
            userId: response.data.user.user_id,
          });
        }
      })
    );
  }

  /**
   * Register new user
   */
  register(data: RegisterRequest): Observable<RegisterResponse> {
    this.logger.info('AuthService: Registration attempt', {
      email: data.email,
    });

    return this.post<RegisterResponse>('/register', {
      name: data.name,
      email: data.email,
      password: data.password,
      agreed_to_terms: data.agreed_to_terms,
    }).pipe(
      tap((response) => {
        if (
          response.success &&
          response.data?.user &&
          response.data?.access_token
        ) {
          // Auto-login user after successful registration
          this.handleAuthSuccess(
            response.data.user,
            response.data.access_token,
            false, // Don't remember me by default for registration
            response.data.refresh_token
          );
          this.logger.info('Registration successful - user auto-logged in', {
            userId: response.data.user.user_id,
          });
        }
      })
    );
  }

  /**
   * Request password reset (send OTP)
   */
  requestPasswordReset(data: PasswordResetRequest): Observable<ApiResponse> {
    this.logger.info('AuthService: Password reset requested', {
      email: data.email,
    });

    return this.post<ApiResponse>('/password-reset/request', data);
  }

  /**
   * Verify OTP
   */
  verifyOTP(data: OtpVerificationRequest): Observable<ApiResponse> {
    this.logger.info('AuthService: OTP verification attempt');

    return this.post<ApiResponse>('/password-reset/verify', data);
  }

  /**
   * Update password
   */
  updatePassword(data: PasswordUpdateRequest): Observable<ApiResponse> {
    this.logger.info('AuthService: Password update attempt');

    return this.post<ApiResponse>('/password-reset/reset', data);
  }

  /**
   * Logout user
   */
  logout(): Observable<void> {
    this.logger.info('AuthService: Logout');

    const token =
      this.storage.getItem<string>('auth_token', 'local') ||
      this.storage.getItem<string>('auth_token', 'session');

    return of(void 0).pipe(
      tap(() => {
        // Call logout endpoint if we have a token
        if (token) {
          this.post('/logout', {}).subscribe({
            error: (err) => this.logger.error('Logout API call failed', err),
          });
        }

        this.clearAuthData();
        this.router.navigate(['/auth/login']);
      })
    );
  }

  /**
   * Refresh access token using refresh token
   */
  refreshToken(): Observable<
    ApiResponse<{ access_token: string; refresh_token?: string }>
  > {
    const refreshToken = this.storage.getItem<string>('refresh_token', 'local');

    if (!refreshToken) {
      this.logger.error('No refresh token available');
      return throwError(() => new Error('No refresh token available'));
    }

    return this.post<
      ApiResponse<{ access_token: string; refresh_token?: string }>
    >('/refresh', { refresh_token: refreshToken });
  }

  /**
   * Handle successful authentication
   */
  private handleAuthSuccess(
    user: User,
    accessToken: string,
    rememberMe?: boolean,
    refreshToken?: string
  ): void {
    // Save user and tokens
    this.currentUserSubject.next(user);
    const storageType = rememberMe ? 'local' : 'session';
    this.storage.setItem('user', user, storageType);
    this.storage.setItem(
      'auth_token',
      accessToken,
      storageType
    );

    if (storageType === 'local') {
      this.storage.removeItem('user', 'session');
      this.storage.removeItem('auth_token', 'session');
    } else {
      this.storage.removeItem('user', 'local');
      this.storage.removeItem('auth_token', 'local');
    }

    // Save refresh token if provided
    if (refreshToken) {
      this.storage.setItem('refresh_token', refreshToken, 'local');
    }

    this.logger.info('Auth data saved successfully', { userId: user.user_id });
  }

  /**
   * Load user from storage
   */
  private loadUserFromStorage(): void {
    const user =
      this.storage.getItem<User>('user', 'local') ||
      this.storage.getItem<User>('user', 'session');
    const token =
      this.storage.getItem<string>('auth_token', 'local') ||
      this.storage.getItem<string>('auth_token', 'session');

    if (user && token) {
      this.currentUserSubject.next(user);
      this.logger.debug('AuthService: User loaded from storage', {
        userId: user.user_id,
      });
    }
  }

  /**
   * Clear authentication data
   */
  private clearAuthData(): void {
    this.currentUserSubject.next(null);
    this.storage.removeItem('user', 'local');
    this.storage.removeItem('user', 'session');
    this.storage.removeItem('auth_token', 'local');
    this.storage.removeItem('auth_token', 'session');
    this.storage.removeItem('refresh_token', 'local');
  }

  private persistUser(user: User): void {
    const storageType = this.getActiveStorageType();
    this.storage.setItem('user', user, storageType);
  }

  private getActiveStorageType(): StorageType {
    if (this.storage.hasItem('auth_token', 'session')) {
      return 'session';
    }

    return 'local';
  }
}
